import { useState } from "react";
import type { Auction } from "@/services/types";
import { Select, type SelectOption } from "@/components/base/Select";
import { CurrencyInput } from "@/components/currency/CurrencyInput";
import { Button } from "@/components/base/Button";
import { Alert } from "@/components/base/Alert";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";
import { computeLivePreview, validateBid, discountPercentLabel } from "@/utils/auction";

interface BidEntryProps {
  auction: Auction;
  currentLowest: number | null;
  onRecord: (memberId: string, amount: number) => void;
}

/**
 * The bid entry form. The amount field is labelled unambiguously as the amount
 * the member will RECEIVE — never as a discount — and shows the resulting
 * discount, dividend and installment live as staff type. Bids are validated
 * before they can be recorded.
 */
export function BidEntry({ auction, currentLowest, onRecord }: BidEntryProps) {
  const [memberId, setMemberId] = useState("");
  const [discountInput, setDiscountInput] = useState<number | null>(null);
  const [receiveInput, setReceiveInput] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const options: SelectOption[] = auction.eligible.map((m) => ({
    value: m.memberId,
    label: `${m.ticketNumber} · ${m.fullName}`,
  }));

  const handleDiscountChange = (val: number | null) => {
    setDiscountInput(val);
    if (val == null || !Number.isFinite(val)) {
      setReceiveInput(null);
    } else {
      setReceiveInput(Math.max(0, auction.pool - val));
    }
  };

  const handleReceiveChange = (val: number | null) => {
    setReceiveInput(val);
    if (val == null || !Number.isFinite(val)) {
      setDiscountInput(null);
    } else {
      setDiscountInput(Math.max(0, auction.pool - val));
    }
  };

  const preview =
    receiveInput != null && receiveInput > 0
      ? computeLivePreview({
          pool: auction.pool,
          monthlyInstallment: auction.monthlyInstallment,
          memberCount: auction.memberCount,
          dividendTiming: auction.dividendTiming,
          amount: receiveInput,
          commissionType: auction.commissionType,
          commissionValue: auction.commissionValue,
        })
      : null;

  const handleRecord = () => {
    if (!memberId) {
      setError("Select the member placing the bid.");
      return;
    }
    if (receiveInput == null || !Number.isFinite(receiveInput)) {
      setError("Enter a valid discount amount or amount member will receive.");
      return;
    }
    const err = validateBid({
      amount: receiveInput,
      pool: auction.pool,
      currentLowest,
      bidStep: auction.bidStep,
      minDiscountPct: auction.minDiscountPct,
      maxDiscountPct: auction.maxDiscountPct,
    });
    if (err) {
      setError(err);
      return;
    }
    onRecord(memberId, receiveInput);
    setDiscountInput(null);
    setReceiveInput(null);
    setError(null);
  };

  if (auction.eligible.length === 0) {
    return (
      <Alert variant="warning" title="No members eligible to bid">
        Every member of this group has already won or is not active. The auction cannot receive bids.
      </Alert>
    );
  }

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-4">
      <h3 className="mb-3 flex items-center gap-1.5 text-sm font-semibold text-foreground-900">
        <i className="ri-play-list-add-line text-primary-600" />
        Record a bid
      </h3>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-[1.2fr_1fr_1fr_auto]">
        <Select
          label="Member"
          value={memberId}
          onChange={setMemberId}
          options={options}
          placeholder="Select eligible member…"
          searchable
          clearable
        />
        <CurrencyInput
          label="Discount Amount (Boli)"
          helper={currentLowest != null ? `Min discount: ${(auction.pool - currentLowest).toLocaleString("en-IN")}` : "Enter discount amount"}
          value={discountInput}
          onChange={handleDiscountChange}
          placeholder="e.g. 200"
        />
        <CurrencyInput
          label="Amount member will receive"
          helper={currentLowest != null ? `Max receive: ${currentLowest.toLocaleString("en-IN")}` : "Prize money to receive"}
          value={receiveInput}
          onChange={handleReceiveChange}
          placeholder={String(auction.pool)}
        />
        <div className="flex items-end">
          <Button className="w-full sm:w-auto" icon={<i className="ri-hammer-line" />} onClick={handleRecord} disabled={!memberId || receiveInput == null}>
            Record bid
          </Button>
        </div>
      </div>

      {preview ? (
        <div className="mt-3 grid grid-cols-2 gap-2 rounded-md bg-background-100 p-3 sm:grid-cols-4">
          <div>
            <p className="text-xs text-foreground-500">Discount</p>
            <p className="tnum mt-0.5 text-sm font-semibold text-danger-600"><CurrencyDisplay amount={preview.discount} hideSymbol /></p>
            <p className="tnum text-xs text-foreground-400">{discountPercentLabel(preview.discount, auction.pool)}</p>
          </div>
          <div>
            <p className="text-xs text-foreground-500">Dividend / member</p>
            <p className="tnum mt-0.5 text-sm font-semibold text-foreground-900"><CurrencyDisplay amount={preview.perMemberDividend} hideSymbol /></p>
            {preview.dividendRemainder > 0 ? (
              <p className="tnum text-xs text-warning-600">+ {preview.dividendRemainder} remainder</p>
            ) : null}
          </div>
          <div>
            <p className="text-xs text-foreground-500">Net installment</p>
            <p className="tnum mt-0.5 text-sm font-semibold text-primary-700"><CurrencyDisplay amount={preview.netInstallment} hideSymbol /></p>
          </div>
          <div>
            <p className="text-xs text-foreground-500">Total collected</p>
            <p className="tnum mt-0.5 text-sm font-semibold text-foreground-900"><CurrencyDisplay amount={preview.totalCollected} hideSymbol /></p>
          </div>
        </div>
      ) : null}

      {error ? <div className="mt-3"><Alert variant="error">{error}</Alert></div> : null}
    </div>
  );
}